import { Injectable } from '@angular/core';
import { debugLogSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { EventManagerService } from '../event-manager/event-manager.service';
import { SocketService } from '../socket.service';

/**
 * Protokolliert Debug-Meldungen aus Blockly und RAGAI-Events im Live-Debugger (`RAGAI.DEBUG`).
 * @remarks
 * {@link log} wird typischerweise explizit aus Custom-JS/Blockly aufgerufen. Zusätzlich wird {@link logEvent}
 * automatisch für **jedes** ausgelöste `RAGAI.*`-Event aufgerufen (über einen Wildcard-Listener
 * `RAGAI.EVENTS.on("RAGAI.*", ...)`, der beim ersten Zugriff auf `window.RAGAI` registriert wird), sofern das
 * Event-Prefix nicht in `RAGAI_DEBUG_FORWARD_EXCLUDED_PREFIXES` steht (aktuell `RAGAI.DEBUG.*`/`RAGAI.EVENTS.*`,
 * um Duplikate bzw. Listener-Registrierungsrauschen zu vermeiden). Beide Wege senden letztlich per Socket ein
 * `blocklyDebugLog`/`logRagaiEvent`-Ereignis an den Server, das im Live-Debugger der Admin-Oberfläche sichtbar wird.
 */
@Injectable({
  providedIn: 'root'
})
export class DebugManagerService {

  constructor(
    private eventManager: EventManagerService,
    private socketService: SocketService
  ) { }

  /**
   * Schreibt eine Debug-Meldung in den Live-Debugger.
   * @param title Titel/Kurzbeschreibung der Meldung, oder ein Objekt gemäß `debugLogSchema`.
   * @param reason Zusätzlicher Beschreibungstext.
   */
  public log(title: string | z.input<typeof debugLogSchema>, reason: string = ''): void {
    const parsed = debugLogSchema.safeParse(title);
    if (parsed.success && typeof title === 'object') {
      title = parsed.data.title;
      reason = parsed.data.reason;
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.DEBUG.log', { title, reason });
    void this.socketService.getIo().then(io => io.emit('blocklyDebugLog', { title: title as string, reason }));
  }

  /**
   * Protokolliert ein RAGAI-Event im Live-Debugger.
   * @param hook Name des ausgelösten RAGAI-Events.
   * @param params Die Event-Parameter.
   */
  public logEvent(hook: string, params: unknown): void {
    void this.socketService.getIo().then(io => io.emit('logRagaiEvent', { hook, reason: this.formatEventReason(params) }));
  }

  // Falls die Event-Params ein pInput-Feld enthalten (z.B. RAGAI.MSG.prompt / RAGAI.FLOW.requestStart),
  // zeige direkt dessen Wert an statt das gesamte Objekt zu stringifien. Bei einer
  // HistoryItemResponse (z.B. RAGAI.MSG.runComplete / RAGAI.FLOW.requestComplete)
  // interessiert nur der eigentliche Antworttext aus content[0].text.
  private formatEventReason(params: unknown): string {
    if (params && typeof params === 'object') {
      if ('pInput' in params) {
        const pInput = (params as { pInput?: unknown }).pInput;
        if (typeof pInput === 'string') return pInput;
      }
      if ('content' in params) {
        const content = (params as { content?: unknown }).content;
        if (Array.isArray(content) && content.length > 0) {
          const first = content[0] as { text?: unknown } | undefined;
          if (first && typeof first.text === 'string') return first.text;
        }
      }
    }
    return JSON.stringify(params);
  }
}
